import React, { useState, useEffect, useRef } from 'react';
import FocusTrap from 'focus-trap-react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import Button from '../Button';
import Icon from '../Icon';
import TranslationProvider from '../../providers/TranslationProvider';
import Ellipsis from '../../svg/icons/more-horizontal.svg';

import './PopupMenu.scss';

const PopupMenu = ({
  buttonsArr,
  leftAlign,
  ariaLabel,
  height,
  width,
  testId,
}) => {
  const [openMenu, setOpenMenu] = useState(false);
  const timeoutId = useRef();

  // Prevents Can't perform a React state update on an unmounted component error
  // In some user actions, the setTimeout and setOpenMenu can be called if this component is unmounted
  // i.e. closing a menu
  useEffect(() => () => clearTimeout(timeoutId.current), []);

  return (
    <div className="PopupMenu">
      <Button
        className="PopupMenu__more-button"
        aria-expanded={openMenu}
        aria-label={ariaLabel}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpenMenu((val) => !val);
        }}
        type="button"
        variant="no-style"
        data-test-id={testId}
      >
        <Icon
          name="ellipsis"
          component={Ellipsis}
          height={height}
          width={width}
          fill="#2A2A2A"
        />
      </Button>
      <FocusTrap
        active={openMenu}
        focusTrapOptions={{
          initialFocus: false,
          allowOutsideClick: true,
          clickOutsideDeactivates: true,
          returnFocusOnDeactivate: false,
          onDeactivate: () => {
            timeoutId.current = setTimeout(() => { setOpenMenu(false); }, 200);
          },
        }}
      >
        <ul className={cx(
          'PopupMenu__menu-container',
          {
            'PopupMenu__menu-container--open': openMenu,
            right: !leftAlign,
          }
        )}
        >
          {buttonsArr?.map(({
            onClick,
            icon,
            iconProps,
            translationKey,
            text,
            ...btnProps
          }) => (
            <li key={translationKey} className="PopupMenu__menu-item">
              <Button
                onClick={() => {
                  setOpenMenu((val) => !val);
                  onClick();
                }}
                type="button"
                variant="no-style"
                {...btnProps}
              >
                <div className="PopupMenu__menu-item-contents">
                  {icon && <Icon height="20px" width="20px" component={icon} name={text} {...iconProps} />}
                  <TranslationProvider
                    translationKey={translationKey}
                  >
                    <div className="PopupMenu__menu-item-contents-text">
                      {text}
                    </div>
                  </TranslationProvider>
                </div>
              </Button>
            </li>
          ))}
        </ul>
      </FocusTrap>
    </div>
  );
};
PopupMenu.propTypes = {
  ariaLabel: PropTypes.string,
  buttonsArr: PropTypes.arrayOf(PropTypes.shape({
    icon: PropTypes.func,
    iconProps: PropTypes.shape({}),
    onClick: PropTypes.func.isRequired,
    text: PropTypes.string.isRequired,
    translationKey: PropTypes.string.isRequired,
  })).isRequired,
  leftAlign: PropTypes.bool,
  height: PropTypes.string,
  width: PropTypes.string,
  testId: PropTypes.string,
};

PopupMenu.defaultProps = {
  ariaLabel: '',
  leftAlign: false,
  height: '20px',
  width: '20px',
  testId: '',
};

export default PopupMenu;
